<template>
  <div class="dashboard-toolbar-inline">
    <!-- Size Toggle (icon buttons only) -->
    <div class="size-toggle">
      <button
        v-for="(config, key) in sizeConfig"
        :key="key"
        class="size-btn"
        :class="{ active: currentSize === key }"
        :title="config.label"
        @click="$emit('size-change', key)"
      >
        <i :class="config.icon"></i>
      </button>
    </div>

    <!-- Add Widget Dropdown -->
    <div class="add-widget-dropdown">
      <button
        class="add-widget-btn"
        :class="{ disabled: !hasHiddenItems }"
        :disabled="!hasHiddenItems"
        @click.stop="toggleAddMenu"
        :title="!hasHiddenItems ? 'All widgets are visible' : 'Add a widget'"
      >
        <i class="fa fa-plus"></i>
        <span>Add Widget</span>
      </button>
      <div class="widget-dropdown-menu" v-if="showAddMenu && hasHiddenItems">
        <!-- Hidden Stats Section (Tier 1 - Key Metrics) -->
        <template v-if="hiddenStats && hiddenStats.length > 0">
          <div class="dropdown-section-header">Key Metrics</div>
          <div
            v-for="stat in hiddenStats"
            :key="'stat-' + stat.key"
            class="dropdown-item"
            @click.stop="addStat(stat.key)"
          >
            <i :class="stat.icon"></i>
            <span>{{ stat.label }}</span>
          </div>
        </template>
        <!-- Hidden Charts Section (Tier 2 - Charts) -->
        <template v-if="hiddenCharts && hiddenCharts.length > 0">
          <div class="dropdown-section-header">Charts</div>
          <div
            v-for="chart in hiddenCharts"
            :key="'chart-' + chart.id"
            class="dropdown-item"
            @click.stop="addChart(chart.id)"
          >
            <i :class="chart.icon"></i>
            <span>{{ chart.name }}</span>
          </div>
        </template>
        <!-- Hidden Widgets Section (Tier 3) -->
        <template v-if="hiddenWidgets && hiddenWidgets.length > 0">
          <div v-if="(hiddenStats && hiddenStats.length > 0) || (hiddenCharts && hiddenCharts.length > 0)" class="dropdown-section-header">Widgets</div>
          <div
            v-for="widget in hiddenWidgets"
            :key="widget.id"
            class="dropdown-item"
            @click.stop="addWidget(widget.id)"
          >
            <i :class="widget.icon"></i>
            <span>{{ widget.name }}</span>
          </div>
        </template>
      </div>
    </div>

    <!-- Reset Button -->
    <button class="reset-btn" @click="confirmReset" title="Reset to default layout">
      <i class="fa fa-undo"></i>
      <span>Reset</span>
    </button>
  </div>
</template>

<script>
import { SIZE_CONFIG } from './dashboardConfig.js';

export default {
  name: 'DashboardToolbar',

  props: {
    currentSize: {
      type: String,
      default: 'normal'
    },
    hiddenWidgets: {
      type: Array,
      default: () => []
    },
    hiddenStats: {
      type: Array,
      default: () => []
      // Format: [{ key, label, icon }]
    },
    hiddenCharts: {
      type: Array,
      default: () => []
      // Format: [{ id, name, icon }]
    }
  },

  data() {
    return {
      showAddMenu: false,
      sizeConfig: SIZE_CONFIG
    };
  },

  computed: {
    hasHiddenItems() {
      return (this.hiddenWidgets && this.hiddenWidgets.length > 0) ||
             (this.hiddenStats && this.hiddenStats.length > 0) ||
             (this.hiddenCharts && this.hiddenCharts.length > 0);
    }
  },

  methods: {
    toggleAddMenu() {
      this.showAddMenu = !this.showAddMenu;
    },

    addWidget(widgetId) {
      this.$emit('add-widget', widgetId);
      this.showAddMenu = false;
    },

    addStat(statKey) {
      this.$emit('add-stat', statKey);
      this.showAddMenu = false;
    },

    addChart(chartId) {
      this.$emit('add-chart', chartId);
      this.showAddMenu = false;
    },

    confirmReset() {
      if (confirm('Reset dashboard to default layout? This will restore all widgets and their positions.')) {
        this.$emit('reset-layout');
      }
    },

    handleOutsideClick(e) {
      if (this.showAddMenu && !this.$el.contains(e.target)) {
        this.showAddMenu = false;
      }
    }
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  }
};
</script>

<style lang="scss" scoped>
.dashboard-toolbar-inline {
  display: flex;
  align-items: center;
  gap: 12px;
}

.size-toggle {
  display: flex;
  gap: 2px;
  background-color: #f1f5f9;
  padding: 3px;
  border-radius: 6px;

  .size-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background-color: transparent;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s ease;
    color: #94a3b8;

    i {
      font-size: 0.75rem;
    }

    &:hover {
      background-color: #e2e8f0;
      color: #64748b;
    }

    &.active {
      background-color: white;
      color: #3abf94;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }
  }
}

.add-widget-dropdown {
  position: relative;

  .add-widget-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 10px;
    border: 1px solid #e2e8f0;
    background-color: white;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    color: #64748b;
    font-size: 0.75rem;
    font-weight: 500;

    i {
      font-size: 0.6875rem;
    }

    &:hover:not(:disabled) {
      border-color: #3abf94;
      color: #3abf94;
    }

    &:disabled,
    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
      background-color: #f8fafc;

      &:hover {
        border-color: #e2e8f0;
        color: #64748b;
      }
    }
  }

  .widget-dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 180px;
    background-color: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    z-index: 1000;
    overflow: hidden;
  }

  .dropdown-section-header {
    padding: 8px 14px 6px;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background-color: #f8fafc;
    border-bottom: 1px solid #f1f5f9;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    font-size: 0.8125rem;
    color: #334155;

    i {
      width: 16px;
      text-align: center;
      color: #64748b;
      font-size: 0.875rem;
    }

    &:hover {
      background-color: #f8fafc;

      i {
        color: #3abf94;
      }
    }

    &:not(:last-child) {
      border-bottom: 1px solid #f1f5f9;
    }
  }
}

.reset-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: none;
  background-color: transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  color: #94a3b8;
  font-size: 0.75rem;

  i {
    font-size: 0.6875rem;
  }

  &:hover {
    background-color: #fef2f2;
    color: #ef4444;
  }
}
</style>
